/* ══════════════════════════════════════════════════
 *  AdminPage — Layout shell
 *  Sidebar navigation + <Outlet> content area
 * ══════════════════════════════════════════════════ */
import { Outlet, NavLink } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import css from './AdminPage.module.css';
import type { AdminSection } from '../../types/admin';

/* ── Section definitions (drives sidebar + routes) ── */
const SECTIONS: AdminSection[] = [
  { id: 'overview', label: 'Overview', icon: '📊', path: '/admin' },
  { id: 'users', label: 'User Management', icon: '👥', path: '/admin/users' },
  { id: 'pipelines', label: 'Pipeline Management', icon: '🔀', path: '/admin/pipelines' },
  { id: 'fields', label: 'Configure Fields', icon: '📋', path: '/admin/fields' },
  { id: 'defendants', label: 'Defendant Data', icon: '🏛️', path: '/admin/defendants' },
  { id: 'tickets', label: 'Issue Tickets', icon: '🎫', path: '/admin/tickets' },
  { id: 'data-import', label: 'Data Import', icon: '📥', path: '/admin/data-import' },
  { id: 'document-upload', label: 'Document Upload', icon: '📤', path: '/admin/document-upload' },
  { id: 'ledger', label: 'Document Ledger', icon: '📒', path: '/admin/ledger' },
  { id: 'reports', label: 'Preset Reports', icon: '📈', path: '/admin/reports' },
  { id: 'recycle-bin', label: 'Recycle Bin', icon: '🗑️', path: '/admin/recycle-bin' },
  { id: 'draft-templates', label: 'Draft Templates', icon: '📝', path: '/admin/draft-templates' },
  { id: 'email-templates', label: 'Email Templates', icon: '✉️', path: '/admin/email-templates' },
  { id: 'blp', label: 'Bulk Letters (BLP)', icon: '📬', path: '/admin/blp' },
  { id: 'automations', label: 'Automations', icon: '⚡', path: '/admin/automations' },
  { id: 'integrations', label: 'Integrations', icon: '🔌', path: '/admin/integrations' },
  { id: 'data-quality', label: 'Data Quality', icon: '🔍', path: '/admin/data-quality' },
  { id: 'client-portal', label: 'Client Portal', icon: '🌐', path: '/admin/client-portal' },
  { id: 'bulk-reassign', label: 'Bulk Reassign Tasks', icon: '🔁', path: '/admin/bulk-reassign' },
  { id: 'system-config', label: 'System Configuration', icon: '⚙️', path: '/admin/system-config' },
  { id: 'task-templates', label: 'Task Templates', icon: '📋', path: '/admin/task-templates' },
  {
    id: 'billing-operations',
    label: 'Billing Operations',
    icon: '🧾',
    path: '/admin/billing-operations',
  },
  { id: 'firm-branding', label: 'Firm Branding', icon: '🏢', path: '/admin/firm-branding' },
];

export default function AdminPage() {
  const { filterSections, isLoading } = usePermissions();
  const visibleSections = isLoading ? SECTIONS : filterSections(SECTIONS);

  return (
    <div className={css.adminLayout}>
      {/* ── Sidebar ── */}
      <nav className={css.sidebar}>
        <div className={css.sidebarHeader}>
          <div className={css.sidebarTitle}>
            <span>⚙️</span> Administration
          </div>
        </div>
        <ul className={css.navList}>
          {visibleSections.map((s) => (
            <li key={s.id} className={css.navItem}>
              <NavLink
                to={s.path}
                end={s.path === '/admin'}
                className={({ isActive }) => `${css.navLink} ${isActive ? css.navLinkActive : ''}`}
              >
                <span className={css.navIcon}>{s.icon}</span>
                {s.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {!isLoading && visibleSections.length === 0 && (
          <div className={css.noAccess}>
            <span style={{ fontSize: '2rem' }}>🔒</span>
            <p>No Admin Access</p>
            <p style={{ fontSize: '0.8rem', opacity: 0.7 }}>
              Contact your system administrator if you need access.
            </p>
          </div>
        )}
      </nav>

      {/* ── Content ── */}
      <main className={css.content}>
        <Outlet />
      </main>
    </div>
  );
}

/* Re-export for sub-pages to use shared CSS */
export { SECTIONS };
